import { useEffect, useState } from 'react'
import { Box, Stack, Typography, Paper, Button, Alert, CircularProgress } from '@mui/material'
import { adminTemplates } from '../api.js'
import { houseStyleRows, unresolvedLabels } from '../houseStyle.js'

// "House style for 1A eBay" (2026-11-02, server/src/ai/house-style.js): what
// the pre-sale drafts use for this template root, derived from its own
// templates. Read-only; it changes when the templates change. version: bumps
// when a template is saved or deleted, so the panel reads it again. open /
// onToggle: kept by the page, so the panel stays open across a reload.
export default function HouseStylePanel({ root, version = 0, open = false, onToggle = () => {} }) {
  const [profile, setProfile] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!root || !open) return undefined
    let live = true
    setLoading(true)
    setError(null)
    adminTemplates.houseStyle(root)
      .then((p) => { if (live) setProfile(p) })
      .catch((e) => { if (live) setError(e.message) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [root, open, version])

  if (!root) return null
  const rows = houseStyleRows(profile)
  const unresolved = unresolvedLabels(profile)
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }} data-testid="house-style-panel">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>House style for {root}</Typography>
        <Button size="small" onClick={onToggle}>{open ? 'Hide' : 'Show'}</Button>
      </Stack>
      {open && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
            What the pre-sale drafts use for this account, read from its templates. Edit a template to change it.
          </Typography>
          {loading && <CircularProgress size={18} />}
          {error && <Alert severity="error">{error}</Alert>}
          {!loading && profile && (
            <>
              <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, columnGap: 2, rowGap: 0.75 }}>
                {rows.map((r) => (
                  <Box key={r.key} sx={{ display: 'contents' }}>
                    <Typography component="dt" variant="body2" fontWeight={600}>{r.label}</Typography>
                    <Typography component="dd" variant="body2" sx={{ m: 0, whiteSpace: 'pre-wrap', color: r.value === 'none' ? 'text.secondary' : 'text.primary' }} data-testid={`house-style-${r.key}`}>{r.value}</Typography>
                  </Box>
                ))}
              </Box>
              {unresolved.length > 0 && (
                <Alert severity="info" sx={{ mt: 1 }} data-testid="house-style-unresolved">
                  No clear majority in the templates, so the drafts use none: {unresolved.join(', ')}.
                </Alert>
              )}
              {(profile.excluded ?? []).length > 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Left out: {profile.excluded.map((x) => `${x.field} (${x.reason})`).join('; ')}.
                </Alert>
              )}
            </>
          )}
        </Box>
      )}
    </Paper>
  )
}
